import React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { CheckCircle, FileText, UserCheck, Calendar, ClipboardList, Stethoscope, Pill, Star, Quote } from "lucide-react";
import ContactForm from "@/components/ContactForm";

const LandingPage = () => {
  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const services = [
    {
      title: "LessMed RationalRx™",
      subtitle: "Deep Prescription Analysis",
      description: "Comprehensive review of medications to optimize therapy, identify risks, and reduce polypharmacy.",
      features: [
        "Complete medication review & dosage analysis",
        "Drug interaction screening & safety alerts",
        "Evidence-based deprescription planning",
        "Cost optimization recommendations"
      ],
      actionLabel: "Get Started"
    },
    {
      title: "LessMed Precision Panel™",
      subtitle: "Rational Supplementation",
      description: "Personalized nutrition through comprehensive functional blood panels and AI insights.",
      features: [
        "Data-driven biomarker analysis",
        "Doctor-verified supplement protocols",
        "Personalized nutrition recommendations",
        "Ongoing monitoring & adjustments"
      ],
      actionLabel: "Book Consultation"
    }
  ];

  const testimonials = [
    {
      name: "Rajesh Kumar",
      age: 58,
      condition: "Diabetes & Hypertension",
      quote: "After 5 years of constant fatigue and dizziness, LessMed identified harmful drug interactions in my prescriptions. Within 3 months of their deprescription plan, I feel energetic again and my side effects are completely gone!",
      benefit: "Eliminated Side Effects",
      rating: 5
    },
    {
      name: "Priya Sharma",
      age: 42,
      condition: "Thyroid & Arthritis",
      quote: "I was spending ₹6,000 monthly on medications. LessMed's cost optimization reduced it to ₹3,500 without compromising my health. They found alternatives and removed unnecessary supplements. A game changer!",
      benefit: "Saved ₹30,000 Annually",
      rating: 5
    },
    {
      name: "Anil Mehta",
      age: 35,
      condition: "Lifestyle Optimization",
      quote: "The Precision Panel™ was eye-opening. My blood work revealed deficiencies I never knew existed. With their personalized supplement protocol, my energy levels doubled, sleep improved, and I finally achieved my fitness goals!",
      benefit: "Achieved Full Health Potential",
      rating: 5
    }
  ];

  const howItWorks = [
    {
      icon: <ClipboardList className="w-10 h-10" />,
      title: "Intake Form",
      description: "Share your medications and health details"
    },
    {
      icon: <UserCheck className="w-10 h-10" />,
      title: "AI + Doctor Review",
      description: "Our experts analyze your prescriptions"
    },
    {
      icon: <FileText className="w-10 h-10" />,
      title: "Report Generation",
      description: "Receive detailed PDF recommendations"
    },
    {
      icon: <Calendar className="w-10 h-10" />,
      title: "Follow-up Consultation",
      description: "Book a session with our doctors"
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-teal-50 via-blue-50 to-indigo-100 px-6 py-20 md:py-32 overflow-hidden">
        {/* Decorative Background Elements */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-teal-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse delay-700"></div>
        
        <div className="max-w-6xl mx-auto relative z-10">
          {/* Trust Badges */}
          <div className="flex flex-wrap justify-center gap-4 md:gap-8 mb-6">
            <span className="px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full text-sm font-semibold text-teal-700 shadow-md border border-teal-200">
              Doctor-Led Platform
            </span>
            <span className="px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full text-sm font-semibold text-blue-700 shadow-md border border-blue-200">
              Evidence-Based
            </span>
            <span className="px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full text-sm font-semibold text-indigo-700 shadow-md border border-indigo-200">
              Patient-Focused
            </span>
          </div>

          {/* Brand Logo */}
          <div className="flex justify-center mb-8">
            <div className="bg-white rounded-2xl shadow-2xl p-4 border-2 border-teal-400">
              <img 
                src="https://customer-assets.emergentagent.com/job_precision-panel-1/artifacts/1xblxc3f_Screenshot%202025-11-03%20224752.png" 
                alt="LessMed Logo" 
                className="h-16 w-auto"
              />
            </div>
          </div>

          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              India&apos;s first <span className="bg-gradient-to-r from-teal-600 to-blue-600 bg-clip-text text-transparent">deprescription</span> and <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">precision supplementation</span> platform
            </h1>
            <p className="text-lg text-gray-700 mb-4 max-w-3xl mx-auto leading-relaxed font-medium">
              Led by doctors to reduce unnecessary medications, identify safer alternatives, and personalize your supplement needs through evidence-based analysis.
            </p>
            <p className="text-base text-teal-700 mb-10 font-semibold">
              Deep medication analysis led by doctors
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                data-testid="book-consultation-btn"
                onClick={scrollToContact}
                size="lg"
                className="bg-gradient-to-r from-teal-500 to-blue-600 hover:from-teal-600 hover:to-blue-700 text-white px-8 py-6 text-lg rounded-full shadow-2xl transition-all hover:shadow-xl hover:scale-105"
              >
                <Stethoscope className="w-5 h-5 mr-2" />
                Book a Consultation
              </Button>
              <Button
                data-testid="submit-prescription-btn"
                onClick={scrollToContact}
                size="lg"
                variant="outline"
                className="border-2 border-teal-600 text-teal-700 hover:bg-teal-50 px-8 py-6 text-lg rounded-full transition-all hover:scale-105 bg-white"
              >
                <Pill className="w-5 h-5 mr-2" />
                Submit Your Prescription
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="px-6 py-20 bg-white" id="about">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">About LessMed</h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            LessMed is building a healthcare revolution — not just another app. Our mission: <span className="font-semibold text-teal-600">Right Meds. Better Health.</span>
          </p>
          <p className="text-base text-gray-600 leading-relaxed mb-6">
            We help patients and doctors reduce polypharmacy, side effects, and unnecessary medications through a science-backed, doctor-led approach that puts your health first.
          </p>
          <p className="text-base text-gray-700 leading-relaxed bg-teal-50 p-6 rounded-2xl border-l-4 border-teal-500">
            Founded and led by medical doctors, LessMed is a trusted ally for both doctors and patients, never replacing the treating physician. More than telemedicine or second opinions, LessMed is a movement toward rational prescribing, safer care, and healthier lives.
          </p>
        </div>
      </section>

      {/* RationalRx Section */}
      <section className="px-6 py-20 bg-gradient-to-b from-white to-teal-50" id="rationalrx">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-10">
            Still on the same medications… year after year?
          </h2>
          <div className="space-y-4 mb-10">
            <p className="text-lg text-gray-700 text-center">On multiple medications for years? Or lifelong?</p>
            <p className="text-lg text-gray-700 text-center">Wonder why you&apos;re still taking certain drugs?</p>
            <p className="text-lg text-gray-700 text-center">New medicines added… but none ever stopped?</p>
            <p className="text-lg text-gray-700 text-center">Monthly medicine bills adding up?</p>
            <p className="text-lg text-gray-700 text-center">Worried about long-term side effects?</p>
          </div>
          <div className="bg-white p-6 rounded-2xl border-l-4 border-teal-500 shadow-md">
            <p className="text-base text-gray-700 leading-relaxed text-center">
              <span className="font-semibold text-teal-700">LessMed RationalRx™</span> offers a doctor-led, evidence-based prescription analysis to simplify therapy, reduce risk, and ensure every medication still makes sense.
            </p>
          </div>
        </div>
      </section>

      {/* Precision Panel Section */}
      <section className="px-6 py-20 bg-teal-50" id="precisionpanel">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-10">
            Supplementing with clarity… or just guessing?
          </h2>
          <div className="space-y-4 mb-10">
            <p className="text-lg text-gray-700 text-center">Confused by social-media health gurus?</p>
            <p className="text-lg text-gray-700 text-center">DIY-ing supplements for months… or years?</p>
            <p className="text-lg text-gray-700 text-center">Taking vitamins &apos;just in case&apos;?</p>
            <p className="text-lg text-gray-700 text-center">Unsure what your body actually needs?</p>
            <p className="text-lg text-gray-700 text-center">Ever had your blood work interpreted for supplementation?</p>
          </div>
          <div className="bg-white p-6 rounded-2xl border-l-4 border-blue-500 shadow-md">
            <p className="text-base text-gray-700 leading-relaxed text-center">
              <span className="font-semibold text-blue-700">LessMed Precision Panel™</span> delivers science-backed, doctor-verified supplementation guided by your biomarkers — not trends, hype, or guesswork.
            </p>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="px-6 py-20 bg-gradient-to-b from-teal-50 to-white" id="services">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">Our Services</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, idx) => (
              <Card
                key={idx}
                data-testid={`service-card-${idx}`}
                className="border-2 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 rounded-2xl bg-white"
              >
                <CardHeader>
                  <CardTitle className="text-2xl text-gray-900">{service.title}</CardTitle>
                  <CardDescription className="text-base text-gray-600 font-medium">{service.subtitle}</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 mb-6">{service.description}</p>
                  <ul className="space-y-3 mb-6">
                    {service.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    data-testid={`service-${idx}-action-btn`}
                    onClick={scrollToContact}
                    className="w-full bg-gradient-to-r from-teal-500 to-blue-600 hover:from-teal-600 hover:to-blue-700 text-white rounded-full"
                  >
                    {service.actionLabel}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="px-6 py-20 bg-gradient-to-br from-blue-50 via-indigo-50 to-teal-50" id="how-it-works">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-16">How It Works</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {howItWorks.map((step, idx) => (
              <div
                key={idx}
                data-testid={`how-it-works-step-${idx}`}
                className="flex flex-col items-center text-center bg-white p-8 rounded-2xl shadow-md hover:shadow-xl transition-all"
              >
                <div className="w-20 h-20 bg-gradient-to-br from-teal-400 to-blue-600 rounded-full flex items-center justify-center text-white mb-6">
                  {step.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{step.title}</h3>
                <p className="text-sm text-gray-600">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="px-6 py-20 bg-white" id="testimonials">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Patient Success Stories</h2>
            <p className="text-base text-gray-600 max-w-2xl mx-auto">
              Real results from real people who transformed their health with LessMed
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, idx) => (
              <Card
                key={idx}
                data-testid={`testimonial-card-${idx}`}
                className="border-2 hover:shadow-2xl transition-all duration-300 rounded-2xl bg-gradient-to-br from-white to-teal-50 relative overflow-hidden"
              >
                <div className="absolute top-4 right-4 opacity-10">
                  <Quote className="w-16 h-16 text-teal-600" />
                </div>
                <CardHeader>
                  <div className="flex items-center gap-2 mb-2">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <CardTitle className="text-xl text-gray-900">{testimonial.name}, {testimonial.age}</CardTitle>
                  <CardDescription className="text-sm text-gray-600 font-medium">{testimonial.condition}</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 text-sm leading-relaxed mb-4 italic">
                    &ldquo;{testimonial.quote}&rdquo;
                  </p>
                  <div className="bg-teal-100 border-l-4 border-teal-600 p-3 rounded">
                    <p className="text-sm font-semibold text-teal-900">{testimonial.benefit}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="px-6 py-20 bg-gradient-to-b from-teal-50 to-white" id="contact">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Ready to Optimize Your Health?</h2>
            <p className="text-base text-gray-600 max-w-2xl mx-auto">
              Fill out the form below and our team will reach out to discuss how LessMed can help you
            </p>
          </div>
          <ContactForm />
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white px-6 py-12">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <h3 className="text-xl font-bold mb-4">LessMed</h3>
              <p className="text-sm text-gray-400">India&apos;s first doctor-led deprescription platform.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="#about" className="hover:text-white transition-colors">About</a></li>
                <li><a href="#services" className="hover:text-white transition-colors">Services</a></li>
                <li><a href="#how-it-works" className="hover:text-white transition-colors">How It Works</a></li>
                <li><a href="#contact" className="hover:text-teal-400 transition-colors">Contact Us</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <p className="text-sm text-gray-400">Email: info.lessmed@gmail.com</p>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8">
            <h4 className="font-semibold mb-3 text-yellow-400">Legal Disclaimer</h4>
            <p className="text-xs text-gray-400 leading-relaxed mb-4">
              LessMed provides advisory services only for basic users. We do not replace your treating physician. Always consult your doctor before making any medication changes. If you opt for consultancy services, you will be seen by our specialist doctors. You become their patient, and medication decisions are made collectively with your treating doctor. All recommendations are evidence-based and subject to medical oversight.
            </p>
            <p className="text-xs text-red-400 leading-relaxed font-medium">
              <strong>Medical Emergency:</strong> For urgent medical situations, contact your doctor immediately or call emergency services. LessMed is not for emergency medical advice.
            </p>
          </div>
          <div className="text-center text-sm text-gray-500 mt-8">
            © 2025 LessMed. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
